import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Quiz as QuizIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material'
import { ComplianceRule } from '../../types/compliance'

interface ComplianceRuleCardProps {
  rule: ComplianceRule
  onEdit: (rule: ComplianceRule) => void
  onDelete: (rule: ComplianceRule) => void
  onGenerateTest: (rule: ComplianceRule) => void
  onViewTests?: (rule: ComplianceRule) => void
  canEdit: boolean
  canDelete: boolean
}

export const ComplianceRuleCard: React.FC<ComplianceRuleCardProps> = ({
  rule,
  onEdit,
  onDelete,
  onGenerateTest,
  onViewTests,
  canEdit,
  canDelete,
}) => {
  const { t, i18n } = useTranslation('compliance')
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    onEdit(rule)
    handleMenuClose()
  }

  const handleDelete = () => {
    onDelete(rule)
    handleMenuClose()
  }

  const handleGenerateTest = () => {
    onGenerateTest(rule)
    handleMenuClose()
  }

  const handleViewTests = () => {
    if (onViewTests) {
      onViewTests(rule)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" component="h3" gutterBottom>
            {rule.title}
          </Typography>
          <IconButton size="small" onClick={handleMenuClick} aria-label="more actions">
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {rule.description}
        </Typography>

        {rule.content && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t('detail.content')}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              paragraph
              sx={{
                whiteSpace: 'pre-wrap',
                maxHeight: '150px',
                overflow: 'auto',
                backgroundColor: 'grey.50',
                padding: 1,
                borderRadius: 1,
                fontSize: '0.875rem',
              }}
            >
              {rule.content}
            </Typography>
          </Box>
        )}

        <Box mb={2}>
          <Chip
            label={rule.category}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ mr: 1, mb: 1 }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t('detail.version')} {rule.version}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('detail.updated')}: {formatDate(rule.updatedAt)}
          </Typography>
        </Box>

        {rule._count && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {t('detail.relatedTests', { count: rule._count.tests })}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between' }}>
        <Button size="small" startIcon={<QuizIcon />} onClick={handleGenerateTest} color="primary">
          {t('card.generateTest')}
        </Button>
        {onViewTests && rule._count && rule._count.tests > 0 && (
          <Button
            size="small"
            startIcon={<ListAltIcon />}
            onClick={handleViewTests}
            color="secondary"
          >
            {t('card.viewTests')}
          </Button>
        )}
      </CardActions>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: '20ch',
          },
        }}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {t('edit.button')}
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('delete.button')}
          </MenuItem>
        )}
      </Menu>
    </Card>
  )
}
